import { MouseEventHandler } from 'react';

import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { IconButton } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import { CSSObject, styled, Theme } from '@mui/material/styles';

import GoLogo from '@/components/GoLogo';
import NavBar from '@/components/NavBar';
import ExpiryCountdown from '@/components/NavBar/components/ExpiryCountdown';
import {
  NavigationProvider,
  useNavigationContext
} from '@/components/NavBar/context/NavigationContext';

const drawerWidth = 285;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  width: `calc(${theme.spacing(9)} + 1px)`
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3, 2)
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open'
})(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme)
      }
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme)
      }
    }
  ]
}));

const NavigationMenu = () => {
  const { isMenuOpen, toggle, open } = useNavigationContext();
  const handleToggleDrawer: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
    toggle();
  };

  const handleDrawerOpen = () => {
    open();
  };

  return (
    <Drawer
      open={isMenuOpen}
      sx={{
        '& .MuiDrawer-paper': {
          backgroundColor: 'background.default'
        }
      }}
      variant='permanent'
      onClick={handleDrawerOpen}
    >
      <DrawerHeader sx={{ justifyContent: 'space-between' }}>
        <GoLogo
          open={isMenuOpen}
          size={40}
          onClick={handleToggleDrawer}
        />
        {isMenuOpen && (
          <IconButton
            sx={{
              cursor: 'w-resize',
              mr: -1.5
            }}
            onClick={handleToggleDrawer}
          >
            <MenuOpenIcon />
          </IconButton>
        )}
      </DrawerHeader>
      {isMenuOpen && <ExpiryCountdown />}
      <NavBar />
    </Drawer>
  );
};

const WrappedNavigationMenu = () => {
  return (
    <NavigationProvider>
      <NavigationMenu />
    </NavigationProvider>
  );
};

export default WrappedNavigationMenu;
